import type { ClickedOverlayFeature, MapEngine } from '@mighty-map/map-engine';
import { create } from 'zustand';
import type { PopupContent } from '../../lib/popup';
import { useMapStore } from '../../state/mapStore';

/**
 * Popups for the layers tools draw themselves as engine overlays: the Coverage HUD's analysis layers and Data
 * Passport's imports, which production added to the map as client-side layers with their own popup templates. The
 * tool that draws an overlay says what a click on one of its features shows (`setOverlayPopups`); a click whose
 * topmost identifiable feature has a popup opens it in place of the identify popup (overlays draw above project
 * layers, so that feature is the one on top). While a tool is drawing, clicks are its own, as for identify.
 */

/** A popup: the heading, then field rows (production's "fields" content) and lines of text (its text content). */
export interface OverlayPopupContent extends PopupContent {
  lines: string[];
}

/** What a click on one of an overlay's features shows; null for nothing (the click falls through, as production's
 *  layers without a popup template let it). */
export type OverlayDescriber = (hit: ClickedOverlayFeature) => OverlayPopupContent | null;

export interface OpenOverlayPopup {
  overlayId: string;
  /** The click, in map-container pixels. */
  point: { x: number; y: number };
  content: OverlayPopupContent;
}

interface OverlayPopupState {
  popup: OpenOverlayPopup | null;
  close: () => void;
}

export const useOverlayPopup = create<OverlayPopupState>()((set) => ({
  popup: null,
  close: () => set({ popup: null }),
}));

const describers = new Map<string, OverlayDescriber>();

/**
 * Say what clicks on an overlay's features show, each time the overlay is drawn (indexes are into the list drawn),
 * or stop (null) when the overlay goes, which closes its open popup. An open popup holds what the click found, so a
 * redraw (the towers demo's date, a recompute) leaves it open.
 */
export function setOverlayPopups(overlayId: string, describe: OverlayDescriber | null): void {
  if (describe) {
    describers.set(overlayId, describe);
    return;
  }
  describers.delete(overlayId);
  const { popup, close } = useOverlayPopup.getState();
  if (popup?.overlayId === overlayId) close();
}

/** The popup for a click: the topmost identifiable feature that has one. */
export function popupFor(
  hits: readonly ClickedOverlayFeature[],
  describe: (overlayId: string) => OverlayDescriber | undefined = (id) => describers.get(id),
): { overlayId: string; content: OverlayPopupContent } | null {
  for (const hit of hits) {
    const content = describe(hit.overlayId)?.(hit) ?? null;
    if (content) return { overlayId: hit.overlayId, content };
  }
  return null;
}

const watched = new WeakSet<MapEngine>();

/** Answer clicks on an engine's identifiable overlays (once per engine; the engine lives for the session). */
export function watchOverlayClicks(engine: MapEngine | null): void {
  if (!engine || watched.has(engine)) return;
  watched.add(engine);
  engine.onClick((e) => {
    if (engine.draw.isActive()) return;
    const found = popupFor(e.overlays ?? []);
    if (!found) {
      useOverlayPopup.getState().close();
      return;
    }
    useOverlayPopup.setState({ popup: { ...found, point: e.point } });
    // Identify has answered the same click with the project feature beneath, if any: the overlay is on top of it.
    queueMicrotask(() => useMapStore.getState().setPopup(null));
  });
}
